var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { createRef, useCallback, useEffect, } from 'react';
import { Menu } from '@headlessui/react';
import DotsHorizontalIcon from '@heroicons/react/outline/DotsHorizontalIcon.js';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useBackgroundContext } from '../BackgroundContext/index.js';
import { Hotkey } from '../Hotkey/Hotkey.js';
import { Icon } from '../Icon/index.js';
import { ChevronDownIcon } from '../icons/index.js';
import { FloatingContextProvider, FloatingWrapper, useFloatingContext, } from '../utils/floating/index.js';
import { runIfFn } from '../utils/index.js';
import { DropdownProvider, useDropdown } from './DropdownContext.js';
export const Dropdown = ({ children, as, className, placement = 'bottom-start', }) => {
    return (React.createElement(Menu, { as: as || 'div', className: twMerge(clsx('relative inline-block text-left', className)) }, renderProps => (React.createElement(FloatingContextProvider, { placement: placement },
        React.createElement(DropdownProvider, { open: renderProps.open }, runIfFn(children, renderProps))))));
};
const Button = props => {
    const { children, icon, className, disabled } = props, rest = __rest(props, ["children", "icon", "className", "disabled"]);
    const { reference } = useFloatingContext();
    const { backgroundType } = useBackgroundContext();
    const buttonClsx = [
        backgroundType === 'main' &&
            'text-content-subdued bg-surface hover:bg-surface-hovered',
        backgroundType === 'surface' &&
            'text-content-subdued bg-surface hover:bg-surface-hovered',
        backgroundType === 'secondary' &&
            'text-content-on-topbar bg-topbar hover:bg-topbar',
    ];
    return (React.createElement(Menu.Button, Object.assign({ ref: reference, className: twMerge(clsx('inline-flex justify-center items-center w-full rounded-md border border-line shadow-sm px-4 py-2 text-sm font-medium ', buttonClsx, disabled && 'opacity-60 cursor-default pointer-events-none', 'focus:outline-none focus-visible:ring', className)), disabled: disabled }, rest),
        React.createElement("span", { className: "sr-only" }, "Open options"),
        children,
        React.createElement(Icon, { className: "-me-1 ms-2 h-5 w-5" }, icon || React.createElement(ChevronDownIcon, null))));
};
const ButtonMinimal = props => {
    const { className, children, disabled } = props, rest = __rest(props, ["className", "children", "disabled"]);
    const { reference } = useFloatingContext();
    const { backgroundType } = useBackgroundContext();
    const buttonClsx = [
        backgroundType === 'main' &&
            'text-content-on-background-subdued bg-background hover:text-content-on-background-hovered',
        backgroundType === 'surface' &&
            'text-content-subdued bg-surface hover:text-content-hovered',
        backgroundType === 'secondary' &&
            'text-content-on-topbar bg-topbar hover:text-content-on-topbar',
    ];
    return (React.createElement(Menu.Button, Object.assign({ ref: reference, className: twMerge(clsx('rounded-full flex items-center', buttonClsx, disabled && 'opacity-60 cursor-default pointer-events-none', 'focus:outline-none focus-visible:ring', className)), disabled: disabled }, rest), children || (React.createElement(React.Fragment, null,
        React.createElement("span", { className: "sr-only" }, "Open options"),
        React.createElement(DotsHorizontalIcon, { className: "h-5 w-5", "aria-hidden": "true" })))));
};
const Items = props => {
    const { children, className, width = 'md' } = props, rest = __rest(props, ["children", "className", "width"]);
    const { open } = useDropdown();
    const { triggerRef } = useFloatingContext();
    const contentNode = createRef();
    const close = useCallback(() => {
        var _a;
        (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, [triggerRef]);
    const focusContent = () => {
        if (contentNode == null) {
            return;
        }
        requestAnimationFrame(() => {
            if (contentNode.current == null) {
                return;
            }
            contentNode.current.focus();
        });
    };
    const FocusTrigger = () => {
        useEffect(() => {
            focusContent();
        }, []);
        return null;
    };
    return (React.createElement(FloatingWrapper, { open: open, skipTransition: true },
        React.createElement(Menu.Items, Object.assign({ ref: contentNode, static: true, className: clsx(width === 'md' && 'min-w-[224px] sm:max-w-xs', 'py-1 rounded-md shadow-lg bg-surface border border-line', 'focus-visible:ring focus:outline-none', className) }, rest),
            open && React.createElement(FocusTrigger, null),
            runIfFn(children, { close }))));
};
const Item = props => {
    const { as, children, leadingIcon, disabled, className, hotkey } = props, rest = __rest(props, ["as", "children", "leadingIcon", "disabled", "className", "hotkey"]);
    const Component = as || 'button';
    const type = Component === 'button' ? 'button' : undefined;
    return (React.createElement(Menu.Item, { disabled: disabled }, ({ active }) => (React.createElement(Component, Object.assign({ type: type, className: clsx(active
            ? 'bg-surface-hovered text-content-hovered'
            : 'bg-surface text-content-subdued', 'flex items-center px-4 py-2 w-full', disabled
            ? 'opacity-60 cursor-default pointer-events-none'
            : 'cursor-pointer', className) }, rest),
        leadingIcon && (React.createElement(Icon, { className: "me-3 h-5 w-5 text-content-subdued group-hover:text-content-hovered shrink-0" }, leadingIcon)),
        React.createElement("span", { className: "grow text-left min-w-0 truncate break-words" }, children),
        hotkey && React.createElement(Hotkey, { className: "ms-3 shrink-0" }, hotkey)))));
};
Dropdown.Button = Button;
Dropdown.ButtonMinimal = ButtonMinimal;
Dropdown.Items = Items;
Dropdown.Item = Item;
//# sourceMappingURL=Dropdown.js.map